import React, { useState } from 'react'
import ButtonLink from '../Buttons/ButtonLink'
import { useNavigate } from 'react-router-dom'

interface FaqItem {
  question: string
  answer: string
}

const faqItemsData: FaqItem[] = [
  {
    question: 'Czy mogę połączyć Kreatlo z innymi narzędziami (np. AutoCAD, Revit, SketchUp)?',
    answer: 'Obecnie nie oferujemy bezpośrednich integracji, ale umożliwiamy import danych oraz załączanie plików projektowych. Integracje są planowane w roadmapie rozwoju.'
  },
  {
    question: 'Czy listy lub dane projektowe są prywatne?',
    answer: 'Tak. Tylko Ty i zaproszeni użytkownicy mają dostęp do danych projektowych lub list. Klienci widzą tylko to, co im udostępnisz.'
  },
  {
    question: 'Czy klienci/inwestorzy muszą się logować?',
    answer: 'Nie muszą! Twoi klienci mogą: przeglądać listę w przeglądarce, dodawać komentarze do konkretnych produktów, podejmować decyzje bez konieczności rejestracji czy pobierania aplikacji.'
  },
  {
    question: 'Czym różni się wasza aplikacja od tworzenia listy zakupowej w Excel?',
    answer: 'Kreatlo to inteligentna alternatywa dla Excela. Dzięki naszej wtyczce, dodajesz produkty do listy zakupów bez ręcznego kopiowania danych. Wystarczy wejść na stronę sklepu, kliknąć "Dodaj" i gotowe! Wtyczka automatycznie sczytuje nazwę, cenę, zdjęcie i link – z każdej strony sklepu internetowego. To ułatwienie dla każdej firmy, w której projekt to coś więcej niż tylko plik w Excelu. To ogromna oszczędność czasu i brak błędów przy kosztorysowaniu. Po prostu załóż konto, pobierz wtyczkę i zobacz, jak to działa!'
  },
  {
    question: 'Czy członkowie mojego zespołu muszą mieć osobne konta?',
    answer: 'Nie. W planach PLUS i PREMIUM możesz zaprosić członków zespołu do wspólnej pracy na jednym koncie (do 25 użytkowników).'
  },
  {
    question: 'Czy mogę zmienić plan w dowolnym momencie?',
    answer: 'Tak. Możesz przejść na wyższy plan w dowolnym momencie. W przypadku przejścia z planu miesięcznego na roczny różnica zostanie odpowiednio przeliczona.'
  },
  {
    question: 'Czy aplikacja jest skierowana tylko dla projektantów ?',
    answer: 'Nie tylko dla projektantów. Nasze narzędzie powstało z myślą o branży kreatywnej, ale dziś korzystają z niego firmy z różnych sektorów. Dlaczego? Bo wszędzie tam, gdzie pojawiają się zadania, projekty i potrzeba lepszej organizacji pracy, nasza platforma się sprawdza.'
  },
  {
    question: 'Czy platforma posiada wewnętrzny komunikator?',
    answer: 'Tak – platforma została wyposażona w intuicyjny i skuteczny komunikator wewnętrzny, który znacząco ułatwia codzienną komunikację w zespole. Dzięki niemu możesz: prowadzić rozmowy 1:1 lub grupowe bez potrzeby korzystania z zewnętrznych aplikacji, komentować konkretne zadania lub etapy projektu — tam, gdzie toczy się praca, szybko przekazywać uwagi, pliki i decyzje, bez konieczności przeszukiwania e-maili, mieć wszystkie rozmowy powiązane z kontekstem projektu — bez chaosu.'
  },
  {
    question: 'Czy po zakończeniu okresu próbnego stracę swoje listy i dane?',
    answer: 'Nie, nie stracisz żadnych danych. Jeśli zdecydujesz się na opłacenie konta po zakończeniu okresu próbnego, będziesz kontynuować pracę dokładnie tam, gdzie ją przerwałeś. Wszystkie Twoje projekty, dodane produkty do listy oraz wysłane propozycje pozostaną bez zmian i będą nadal dostępne zarówno dla Ciebie, jak i Twoich klientów.'
  },
  {
    question: 'Co jeśli wybiorę plan niższy niż wersja testowa (Plan Premium)?',
    answer: 'W przypadku wyboru planu o niższym zakresie funkcji niż wersja próbna, utracisz dostęp jedynie do funkcjonalności, które nie są dostępne w nowym planie. Twoje dane pozostaną zapisane — nie zostaną usunięte — ale niektóre funkcje mogą zostać tymczasowo zablokowane.'
  }
]

const PlusIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2.5} stroke='currentColor' className='tw-w-5 tw-h-5 tw-text-teal'>
    <path strokeLinecap='round' strokeLinejoin='round' d='M12 4.5v15m7.5-7.5h-15' />
  </svg>
)

const MinusIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2.5} stroke='currentColor' className='tw-w-5 tw-h-5 tw-text-green'>
    <path strokeLinecap='round' strokeLinejoin='round' d='M19.5 12h-15' />
  </svg>
)

function FAQ () {
  const navigate = useNavigate()
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  const halfIndex = Math.ceil(faqItemsData.length / 2)
  const firstHalf = faqItemsData.slice(0, halfIndex)
  const secondHalf = faqItemsData.slice(halfIndex)

  const renderFaqItem = (item: FaqItem, globalIndex: number, isFirstInColumn: boolean) => {
    const isOpen = openFaq === globalIndex
    return (
      <div
        key={globalIndex}
        className={`
              ${isOpen
                ? 'tw-border-t-2 tw-border-green'
                : isFirstInColumn
                    ? 'tw-border-t-0'
                    : 'tw-border-t tw-border-gray-300'
              }
            `}
      >
        <div
          className='tw-flex tw-justify-between tw-items-center tw-py-4 tw-px-2 md:tw-px-3 tw-cursor-pointer'
          onClick={() => toggleFaq(globalIndex)}
        >
          <h4 className={`tw-text-base md:tw-text-lg tw-font-medium tw-flex-grow ${isOpen ? 'tw-text-green' : 'tw-text-teal'}`}>
            {item.question}
          </h4>
          <div className='tw-ml-4 tw-flex-shrink-0'>
            {isOpen ? <MinusIcon /> : <PlusIcon />}
          </div>
        </div>
        {isOpen && (
          <div className='tw-px-2 md:tw-px-3 tw-pb-4'>
            <div className='tw-border-l-2 tw-border-green tw-pl-3 md:tw-pl-4'>
              <p className='tw-text-sm tw-text-slate-600'>
                {item.answer}
              </p>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div id='faq'>
      <section className='tw-container xl:tw-max-w-7xl tw-mx-auto tw-px-4 tw-pt-24 md:tw-pt-28 xl:tw-pt-32 tw-pb-8'>
        <div className='tw-text-center tw-mb-12 lg:tw-mb-16'>
          <h2 className='tw-text-sm tw-font-semibold tw-uppercase tw-text-green tw-mb-2'>Pomoc i wsparcie</h2>
          <h3 className='tw-text-3xl lg:tw-text-4xl tw-font-bold tw-text-teal tw-mb-4'>Najczęściej zadawane pytania</h3>
        </div>

        <div className='tw-flex tw-flex-wrap -tw-mx-2 md:-tw-mx-3'>
          <div className='tw-w-full lg:tw-w-1/2 tw-px-2 md:tw-px-3'>
            {firstHalf.map((item, index) => renderFaqItem(item, index, index === 0))}
          </div>
          <div className='tw-w-full lg:tw-w-1/2 tw-px-2 md:tw-px-3'>
            {secondHalf.map((item, index) => renderFaqItem(item, index + halfIndex, index === 0))}
          </div>
        </div>

        <section className='tw-bg-white tw-pt-6 tw-pb-12 md:tw-pb-10'>
          <div className='tw-container tw-mx-auto tw-px-6 tw-text-center'>
            <h4 className='tw-text-lg md:tw-text-xl xl:tw-text-2xl tw-font-semibold tw-text-slate-800 tw-mb-3'>
              Chcesz wiedzieć więcej?
            </h4>
            <p className='tw-text-xs md:tw-text-sm xl:tw-text-base tw-text-slate-600 tw-max-w-xl tw-mx-auto'>
              Potrzebujesz więcej informacji na temat konkretnej funkcji?
              <br />
              Skontaktuj się z pomocą techniczną, wyślij do nas maila
            </p>
            <a
              href='mailto:help@kreatlo.com'
              className='
                                    tw-mt-4 tw-inline-block tw-text-lg tw-font-semibold
                                    tw-text-teal hover:tw-text-teal/40 hover:tw-underline
                                    tw-transition-colors
                                '
            >
              help@kreatlo.com
            </a>
          </div>
        </section>
      </section>
    </div>
  )
}

export default FAQ
